/**
 * Module list with drag-and-drop reorder (AR-328).
 *
 * One flat sortable list holds module headers and the steps of the expanded
 * modules, which is what lets a step be dragged across module boundaries while
 * a module drag carries its steps with it. System modules and system steps are
 * `disabled` in dnd-kit, so they can be read but never moved.
 */
import React, { useMemo } from 'react';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  GripVertical,
  Lock,
  MoreHorizontal,
  Play,
  Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ActivationModule, ActivationStep } from '@/lib/activationBuilder';
import { RequirementBadge, RouteChips, TypeBadge } from './activationPrimitives';

const MODULE_PREFIX = 'module:';
const STEP_PREFIX = 'step:';

interface ActivationModuleListProps {
  modules: ActivationModule[];
  expandedModuleIds: string[];
  selectedStepKey: string | null;
  onToggleModule: (moduleId: string) => void;
  onSelectModule: (moduleId: string) => void;
  onSelectStep: (stepKey: string) => void;
  onAddStep: (moduleId: string) => void;
  onReorder: (modules: ActivationModule[]) => void;
  onArchiveStep: (stepKey: string) => void;
  /** Reorder is suppressed while the list is filtered — see the page. */
  dragDisabled?: boolean;
}

interface FlatRow {
  id: string;
  kind: 'module' | 'step';
  moduleId: string;
  stepKey?: string;
}

/* ── Rows ────────────────────────────────────────────────────────────────── */

const DragHandle: React.FC<{
  listeners: ReturnType<typeof useSortable>['listeners'];
  attributes: ReturnType<typeof useSortable>['attributes'];
  disabled?: boolean;
  label: string;
}> = ({ listeners, attributes, disabled, label }) =>
  disabled ? (
    <span className="shrink-0 p-0.5 opacity-25" aria-hidden>
      <GripVertical className="h-4 w-4 text-gray-400" />
    </span>
  ) : (
    <button
      type="button"
      {...attributes}
      {...listeners}
      aria-label={label}
      className="shrink-0 cursor-grab rounded p-0.5 text-gray-300 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 dark:text-gray-600 dark:hover:text-gray-400"
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );

const ModuleRow: React.FC<{
  module: ActivationModule;
  expanded: boolean;
  dragDisabled?: boolean;
  onToggle: () => void;
  onSelect: () => void;
  onAddStep: () => void;
}> = ({ module, expanded, dragDisabled, onToggle, onSelect, onAddStep }) => {
  const immovable = !!module.locked || !!dragDisabled;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${MODULE_PREFIX}${module.id}`,
    disabled: immovable,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-t-xl border-x border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900',
        !expanded && 'rounded-b-xl border-b',
        isDragging && 'opacity-60 shadow-lg',
      )}
      data-testid={`activation-module-${module.id}`}
    >
      <DragHandle
        listeners={listeners}
        attributes={attributes}
        disabled={immovable}
        label={`Reorder ${module.name}`}
      />
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
        {module.order}
      </span>

      <div className="min-w-0 flex-1 basis-40 sm:basis-48">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onSelect}
            className={cn(
              'truncate rounded text-base font-semibold hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
              module.locked
                ? 'text-gray-500 dark:text-gray-400'
                : 'text-gray-900 dark:text-gray-100',
            )}
          >
            {module.name}
          </button>
          <span className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
            HDR: {module.navLabel}
          </span>
          {module.locked && (
            <span className="inline-flex items-center gap-1 rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300">
              <Lock className="h-2.5 w-2.5" /> System / Locked
            </span>
          )}
          {module.buildOut && (
            <span className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500">
              Build-out
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
          {module.description}
        </p>
      </div>

      <RouteChips routes={module.routes} />
      <span className="hidden shrink-0 text-xs text-gray-500 sm:inline dark:text-gray-400">
        {module.steps.length} steps
      </span>
      {module.miniTracker ? (
        <Eye className="h-4 w-4 shrink-0 text-gray-400" aria-label="Mini-tracker on" />
      ) : (
        <EyeOff className="h-4 w-4 shrink-0 text-gray-300 dark:text-gray-600" aria-label="Mini-tracker off" />
      )}
      {!module.locked && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onAddStep}
          className="hidden h-7 shrink-0 px-2 text-xs text-orange-600 hover:text-orange-700 md:inline-flex dark:text-orange-400"
        >
          <Plus className="mr-1 h-3 w-3" /> Add step
        </Button>
      )}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${module.name}`}
        className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
    </div>
  );
};

const StepRow: React.FC<{
  step: ActivationStep;
  index: number;
  selected: boolean;
  last: boolean;
  dragDisabled?: boolean;
  onSelect: () => void;
  onArchive: () => void;
}> = ({ step, index, selected, last, dragDisabled, onSelect, onArchive }) => {
  const immovable = !!step.locked || !!dragDisabled;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `${STEP_PREFIX}${step.key}`,
    disabled: immovable,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex flex-wrap items-center gap-x-2.5 gap-y-2 border-x border-b border-gray-200 bg-white px-4 py-2.5 dark:border-gray-700 dark:bg-gray-900',
        last && 'rounded-b-xl',
        selected &&
          'bg-orange-50/70 shadow-[inset_3px_0_0_0_theme(colors.orange.500)] dark:bg-orange-950/20',
        isDragging && 'opacity-60 shadow-lg',
      )}
      data-testid={`activation-step-${step.key}`}
    >
      <DragHandle
        listeners={listeners}
        attributes={attributes}
        disabled={immovable}
        label={`Reorder ${step.name}`}
      />
      <span className="w-5 shrink-0 text-right text-[11px] text-gray-400 dark:text-gray-500">
        {index + 1}
      </span>
      {step.locked && <Lock className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-label="System step" />}

      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'min-w-0 flex-1 basis-40 truncate text-left text-sm font-medium hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
          step.locked ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100',
        )}
      >
        {step.name}
      </button>

      {step.video && (
        <span
          className="shrink-0 rounded bg-orange-100 p-0.5 text-orange-600 dark:bg-orange-950/50 dark:text-orange-400"
          aria-label="Has video"
        >
          <Play className="h-2.5 w-2.5" />
        </span>
      )}
      <TypeBadge type={step.type} />
      <RequirementBadge requirement={step.requirement} />
      {step.pathways && (
        <span className="hidden shrink-0 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[9px] font-semibold text-gray-500 lg:inline dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
          {step.pathways[0]}
        </span>
      )}
      <span className="hidden lg:inline">
        <RouteChips routes={step.routes} activeOnly />
      </span>
      <Eye className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Actions for ${step.name}`}
            className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onSelect}>
            {step.locked ? 'View step' : 'Edit step'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onArchive} disabled={step.locked}>
            Archive step
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

/* ── List ────────────────────────────────────────────────────────────────── */

export const ActivationModuleList: React.FC<ActivationModuleListProps> = ({
  modules,
  expandedModuleIds,
  selectedStepKey,
  onToggleModule,
  onSelectModule,
  onSelectStep,
  onAddStep,
  onReorder,
  onArchiveStep,
  dragDisabled = false,
}) => {
  // Keyboard sensor keeps reorder operable without a mouse: focus a drag
  // handle, Space to lift, arrows to move, Space to drop.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const rows = useMemo<FlatRow[]>(
    () =>
      modules.flatMap((module) => {
        const header: FlatRow = { id: `${MODULE_PREFIX}${module.id}`, kind: 'module', moduleId: module.id };
        if (!expandedModuleIds.includes(module.id)) return [header];
        return [
          header,
          ...module.steps.map<FlatRow>((s) => ({
            id: `${STEP_PREFIX}${s.key}`,
            kind: 'step',
            moduleId: module.id,
            stepKey: s.key,
          })),
        ];
      }),
    [modules, expandedModuleIds],
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (dragDisabled || !over || active.id === over.id) return;

    const from = rows.find((r) => r.id === active.id);
    const to = rows.find((r) => r.id === over.id);
    if (!from || !to) return;

    // Module drag — the whole module moves, its steps travel with it. Dropping
    // onto a locked module would renumber and displace it, so reject that too.
    if (from.kind === 'module') {
      const fromIndex = modules.findIndex((m) => m.id === from.moduleId);
      const toIndex = modules.findIndex((m) => m.id === to.moduleId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
      if (modules[toIndex].locked) return;
      onReorder(arrayMove(modules, fromIndex, toIndex).map((m, i) => ({ ...m, order: i + 1 })));
      return;
    }

    // Step drag — within a module, or onto another module's list.
    const sourceModule = modules.find((m) => m.id === from.moduleId);
    const targetModule = modules.find((m) => m.id === to.moduleId);
    if (!sourceModule || !targetModule || targetModule.locked) return;

    const fromStepIndex = sourceModule.steps.findIndex((s) => s.key === from.stepKey);
    if (fromStepIndex < 0) return;
    const moved = sourceModule.steps[fromStepIndex];

    // Within one module the source is still in the list, so the target index
    // must be read before the step is removed — arrayMove does exactly that.
    if (sourceModule.id === targetModule.id) {
      const toStepIndex =
        to.kind === 'module'
          ? 0
          : sourceModule.steps.findIndex((s) => s.key === to.stepKey);
      if (toStepIndex < 0 || toStepIndex === fromStepIndex) return;
      const reordered = arrayMove(sourceModule.steps, fromStepIndex, toStepIndex);
      onReorder(modules.map((m) => (m.id === sourceModule.id ? { ...m, steps: reordered } : m)));
      return;
    }

    // Across modules the target list is untouched, so a plain splice is right.
    const remaining = sourceModule.steps.filter((s) => s.key !== from.stepKey);
    const insertAt =
      to.kind === 'module'
        ? 0
        : Math.max(0, targetModule.steps.findIndex((s) => s.key === to.stepKey));

    const inserted = [...targetModule.steps];
    inserted.splice(insertAt, 0, {
      ...moved,
      moduleId: targetModule.id,
      moduleName: targetModule.name,
    });

    onReorder(
      modules.map((m) => {
        if (m.id === targetModule.id) return { ...m, steps: inserted };
        if (m.id === sourceModule.id) return { ...m, steps: remaining };
        return m;
      }),
    );
  };

  if (!modules.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-16 text-center dark:border-gray-700 dark:bg-gray-900/50">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">No modules to show</p>
        <p className="mx-auto mt-1 max-w-md text-xs text-gray-500 dark:text-gray-400">
          Nothing matches the current filters. Clear a filter, or add a module to start building the
          member activation flow.
        </p>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-3">
          {modules.map((module) => {
            const expanded = expandedModuleIds.includes(module.id);
            return (
              <div key={module.id}>
                <ModuleRow
                  module={module}
                  expanded={expanded}
                  dragDisabled={dragDisabled}
                  onToggle={() => onToggleModule(module.id)}
                  onSelect={() => onSelectModule(module.id)}
                  onAddStep={() => onAddStep(module.id)}
                />
                {expanded &&
                  (module.steps.length ? (
                    module.steps.map((s, index) => (
                      <StepRow
                        key={s.key}
                        step={s}
                        index={index}
                        selected={selectedStepKey === s.key}
                        last={index === module.steps.length - 1}
                        dragDisabled={dragDisabled}
                        onSelect={() => onSelectStep(s.key)}
                        onArchive={() => onArchiveStep(s.key)}
                      />
                    ))
                  ) : (
                    <div className="rounded-b-xl border-x border-b border-gray-200 bg-white px-4 py-8 text-center dark:border-gray-700 dark:bg-gray-900">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        No steps in this module yet.
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onAddStep(module.id)}
                        className="mt-1 h-7 text-xs text-orange-600 dark:text-orange-400"
                      >
                        <Plus className="mr-1 h-3 w-3" /> Add the first step
                      </Button>
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default ActivationModuleList;
